// Platform Owner — Org Management Drawer
// Right-side drawer that opens when "Manage" is clicked on a row.
// Tabs: Overview · Subscription · Users · Modules
// Every action is RBAC-aware and writes to the platform audit log.
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  X, Users, CreditCard, Layers, Activity, UserPlus, Trash2, ShieldCheck,
  Pause, Play, CheckCircle2, Loader2, Copy, Mail, Phone, Send, KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  { v: 'owner',         label: 'Property Owner (full admin)' },
  { v: 'asset_manager', label: 'Asset Manager' },
  { v: 'pmc',           label: 'Property Manager (PMC)' },
  { v: 'operations',    label: 'Operations Staff' },
  { v: 'maintenance',   label: 'Maintenance Staff' },
  { v: 'finance',       label: 'Finance Staff' },
  { v: 'marketing',     label: 'Marketing Staff' },
  { v: 'investor',      label: 'Investor (read-only)' },
];

export default function OrgManageDrawer({ org, onClose, onMutate }) {
  const [tab, setTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [modules, setModules] = useState({ groups: {}, all_modules: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!org) return;
    setLoading(true);
    try {
      const [u, p, t, m] = await Promise.all([
        api.platformOrgUsers(org.id),
        api.platformPlans(),
        api.platformTemplates(),
        api.platformModules(),
      ]);
      setUsers(u.data.users || []);
      setPlans(p.data || []);
      setTemplates(t.data || []);
      setModules(m.data || { groups: {}, all_modules: [] });
    } catch (e) { toast.error('Failed to load org details'); }
    finally { setLoading(false); }
  }, [org]);

  useEffect(() => { load(); }, [load]);

  // ESC to close
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  if (!org) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} data-testid="org-drawer-backdrop" />
      <aside className="fixed end-0 top-0 h-screen w-full max-w-[680px] bg-white z-50 shadow-2xl flex flex-col" data-testid="org-drawer">
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#E6EAF0] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">Organization</div>
            <h2 className="mt-0.5 text-xl font-semibold tracking-tight text-[#0E1320] truncate" data-testid="org-drawer-name">{org.name}</h2>
            <div className="mt-1 text-[11.5px] text-[#71777E] reva-mono">{org.id}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F4F6F9] rounded-md" data-testid="org-drawer-close">
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b border-[#ECEFF3] flex gap-1 overflow-x-auto">
          {[
            { id: 'overview',     label: 'Overview',     icon: Activity },
            { id: 'subscription', label: 'Subscription', icon: CreditCard },
            { id: 'users',        label: `Users (${users.length})`, icon: Users },
          ].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`org-drawer-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px ${
                  tab === t.id ? 'border-[#D4AF37] text-[#0E1320]' : 'border-transparent text-[#71777E] hover:text-[#0E1320]'
                }`}>
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? <div className="text-center py-10"><Loader2 className="animate-spin h-5 w-5 inline text-[#71777E]" /></div>
          : tab === 'overview' ? <OverviewTab org={org} />
          : tab === 'subscription' ? <SubscriptionTab org={org} plans={plans} modules={modules} onMutate={() => { load(); onMutate?.(); }} />
          : tab === 'users' ? <UsersTab org={org} users={users} templates={templates} onMutate={load} />
          : null}
        </div>
      </aside>
    </>
  );
}

// ─────────── Overview ───────────
function OverviewTab({ org }) {
  const sub = org.subscription;
  return (
    <div className="space-y-5">
      <Section title="Organization">
        <KV label="Name"           value={org.name} />
        <KV label="Type"           value={org.is_sandbox ? 'Sandbox' : 'Production'} />
        <KV label="Currency"       value={org.currency || 'SAR'} />
        <KV label="Status"         value={org.is_suspended ? 'Suspended' : 'Active'} />
        <KV label="Created"        value={org.created_at ? new Date(org.created_at).toLocaleDateString() : '—'} />
      </Section>
      <Section title="Usage">
        <KV label="Users"          value={org.stats?.users ?? 0} />
        <KV label="Properties"     value={org.stats?.properties ?? 0} />
        <KV label="Units"          value={org.stats?.units ?? 0} />
        <KV label="Tenants"        value={org.stats?.tenants ?? 0} />
      </Section>
      {sub && (
        <Section title="Subscription snapshot">
          <KV label="Plan"           value={sub.plan_id} />
          <KV label="Seat limit"     value={sub.seat_limit} />
          <KV label="Properties cap" value={sub.properties_limit} />
          <KV label="Status"         value={sub.status} />
        </Section>
      )}
    </div>
  );
}

// ─────────── Subscription ───────────
function SubscriptionTab({ org, plans, modules, onMutate }) {
  const sub = org.subscription;
  const [planId, setPlanId] = useState(sub?.plan_id || '');
  const [seatLimit, setSeatLimit] = useState(sub?.seat_limit || 5);
  const [propsLimit, setPropsLimit] = useState(sub?.properties_limit || 50);
  const [enabledModules, setEnabledModules] = useState(sub?.modules || []);
  const [saving, setSaving] = useState(false);

  // When plan changes, pull its module defaults
  const onPlanChange = (newPlanId) => {
    setPlanId(newPlanId);
    const p = plans.find(x => x.id === newPlanId);
    if (p?.modules) setEnabledModules(p.modules);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      if (sub) {
        await api.platformUpdateSubscription(sub.id, {
          plan_id: planId, seat_limit: Number(seatLimit), properties_limit: Number(propsLimit),
        });
        // Update modules snapshot too — direct PUT path doesn't allow modules currently;
        // we can extend the endpoint but for now use re-assign:
      } else {
        await api.platformAssignSubscription({
          organization_id: org.id, plan_id: planId, seat_limit: Number(seatLimit), properties_limit: Number(propsLimit),
        });
      }
      toast.success('Subscription saved');
      onMutate();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const toggleModule = (m) => {
    setEnabledModules(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m]);
  };

  return (
    <div className="space-y-5">
      <Section title="Plan & limits">
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label className="text-[11.5px]">Plan</Label>
            <select value={planId} onChange={e => onPlanChange(e.target.value)} className="w-full h-9 px-2 border border-[#E6EAF0] rounded-md text-[13px]" data-testid="sub-plan-select">
              <option value="">Select plan…</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name_en} · SAR {p.price_sar_monthly}/mo · {p.seat_limit} seats</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11.5px]">Seat limit</Label>
              <Input type="number" value={seatLimit} onChange={e => setSeatLimit(e.target.value)} data-testid="sub-seats" />
            </div>
            <div>
              <Label className="text-[11.5px]">Properties limit</Label>
              <Input type="number" value={propsLimit} onChange={e => setPropsLimit(e.target.value)} data-testid="sub-props" />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Modules" subtitle="Toggle which features this organization can access. Plan default is applied automatically when you change the plan.">
        <div className="space-y-2.5 max-h-[280px] overflow-y-auto pr-1">
          {Object.entries(modules.groups || {}).map(([group, items]) => (
            <div key={group}>
              <div className="text-[10px] reva-mono uppercase tracking-[0.18em] text-[#71777E] mb-1">{group}</div>
              <div className="grid grid-cols-2 gap-1">
                {items.map(m => (
                  <label key={m} className="inline-flex items-center gap-1.5 text-[12px] py-1 px-2 rounded hover:bg-[#F4F6F9] cursor-pointer">
                    <input type="checkbox" checked={enabledModules.includes(m)} onChange={() => toggleModule(m)} data-testid={`sub-module-${m}`} />
                    <span className="capitalize">{m.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[10.5px] text-[#71777E]">
          {enabledModules.length} of {modules.all_modules?.length || 0} modules enabled.
        </div>
      </Section>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving || !planId} data-testid="sub-save">
          {saving ? <Loader2 className="animate-spin h-4 w-4 me-2" /> : <CheckCircle2 className="h-4 w-4 me-2" />}
          Save subscription
        </Button>
      </div>
    </div>
  );
}

// ─────────── Users ───────────
function UsersTab({ org, users, templates, onMutate }) {
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({
    email: '', full_name: '', role: 'operations', phone: '', template_id: '',
    auth_method: 'invite_link', password: '',
  });
  const [adding, setAdding] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [resentLink, setResentLink] = useState(null); // {user_id, url}
  const [resetPwId, setResetPwId] = useState(null);
  const [resetPwResult, setResetPwResult] = useState(null); // {user_id, password}

  const onCreate = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const payload = {
        email: addForm.email, full_name: addForm.full_name, role: addForm.role,
        phone: addForm.phone, template_id: addForm.template_id || null,
        auth_method: addForm.auth_method,
        // For password flow: send admin-typed pw if present, else server generates
        password: (addForm.auth_method === 'password' && addForm.password.trim()) ? addForm.password.trim() : null,
      };
      const r = await api.platformCreateOrgUser(org.id, payload);
      setCreatedInfo({
        user: r.data.user,
        auth_method: r.data.auth_method,
        link: r.data.access_link,
        password: r.data.default_password,
      });
      setAddForm({ email: '', full_name: '', role: 'operations', phone: '', template_id: '', auth_method: 'invite_link', password: '' });
      onMutate();
    } catch (e) { toast.error(e?.response?.data?.detail || 'User creation failed'); }
    finally { setAdding(false); }
  };

  const onResendInvite = async (u) => {
    setResendingId(u.id);
    try {
      const r = await api.platformResendInvite(org.id, u.id);
      const link = r.data.access_link?.url;
      setResentLink({ user_id: u.id, url: link });
      if (link) {
        navigator.clipboard.writeText(window.location.origin + link).catch(() => {});
        toast.success('Fresh invite link generated and copied');
      }
      onMutate();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not regenerate invite'); }
    finally { setResendingId(null); }
  };

  const onResetPassword = async (u) => {
    if (!window.confirm(`Set a fresh default password for "${u.email}"? They will need this new password to log in.`)) return;
    setResetPwId(u.id);
    try {
      const r = await api.platformResetUserPassword(org.id, u.id, {});
      setResetPwResult({ user_id: u.id, password: r.data.default_password });
      navigator.clipboard.writeText(r.data.default_password).catch(() => {});
      toast.success('New password generated and copied');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
    finally { setResetPwId(null); }
  };

  const applyTemplate = async (userId, template_id) => {
    if (!template_id) return;
    try {
      await api.platformApplyTemplate(org.id, userId, { template_id, property_scopes: [] });
      toast.success('Permission template applied');
      onMutate();
    } catch (e) { toast.error('Apply failed'); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove user "${u.email}" from this organization?`)) return;
    try {
      await api.platformRemoveUser(org.id, u.id);
      toast.success('User removed');
      onMutate();
    } catch (e) { toast.error('Remove failed'); }
  };

  return (
    <div className="space-y-4">
      {/* Add user */}
      {!showAdd ? (
        <Button onClick={() => setShowAdd(true)} variant="outline" className="w-full" data-testid="org-users-add-toggle">
          <UserPlus size={14} className="me-1.5" /> Add user to this organization
        </Button>
      ) : (
        <form onSubmit={onCreate} className="rounded-xl border border-[#E6EAF0] bg-[#FBFCFD] p-4 space-y-3" data-testid="org-users-add-form">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-[#0E1320]">New user</span>
            <button type="button" onClick={() => { setShowAdd(false); setCreatedInfo(null); }} className="text-[#71777E] hover:text-[#0E1320]"><X size={14} /></button>
          </div>
          {createdInfo ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="org-users-created">
              <div className="text-[12.5px] text-emerald-900 font-medium inline-flex items-center gap-1.5">
                <CheckCircle2 size={14} /> User created — share access details below
              </div>
              <div className="text-[11.5px] text-emerald-800">{createdInfo.user.email}</div>
              {createdInfo.auth_method === 'invite_link' && createdInfo.link && (
                <div>
                  <div className="text-[10.5px] reva-mono uppercase tracking-[0.14em] text-emerald-900 mb-1">Invite link · 7-day expiry</div>
                  <div className="flex items-center gap-1.5">
                    <code className="flex-1 text-[10.5px] bg-white border border-emerald-200 px-2 py-1 rounded reva-mono break-all">{window.location.origin}{createdInfo.link}</code>
                    <button onClick={() => { navigator.clipboard.writeText(window.location.origin + createdInfo.link); toast.success('Invite link copied'); }} className="p-1.5 hover:bg-emerald-100 rounded" data-testid="org-users-copy-link">
                      <Copy size={13} />
                    </button>
                  </div>
                </div>
              )}
              {createdInfo.auth_method === 'password' && createdInfo.password && (
                <div data-testid="org-users-created-password">
                  <div className="text-[10.5px] reva-mono uppercase tracking-[0.14em] text-emerald-900 mb-1 inline-flex items-center gap-1">
                    <KeyRound size={10} /> Default password · share this manually
                  </div>
                  <div className="flex items-center gap-1.5">
                    <code className="flex-1 text-[12px] bg-white border border-emerald-200 px-2 py-1.5 rounded reva-mono font-semibold tracking-wide select-all">{createdInfo.password}</code>
                    <button onClick={() => { navigator.clipboard.writeText(createdInfo.password); toast.success('Password copied'); }} className="p-1.5 hover:bg-emerald-100 rounded" data-testid="org-users-copy-password">
                      <Copy size={13} />
                    </button>
                  </div>
                  <div className="mt-1.5 text-[10.5px] text-emerald-800">
                    The user can log in immediately at <strong>/login/manager</strong> with this password and change it later from their settings.
                  </div>
                </div>
              )}
              <button onClick={() => setCreatedInfo(null)} className="text-[11.5px] text-emerald-900 underline">Add another →</button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Email *" type="email" required value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} data-testid="org-users-add-email" />
                <Input placeholder="Full name *" required value={addForm.full_name} onChange={e => setAddForm({ ...addForm, full_name: e.target.value })} data-testid="org-users-add-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={addForm.role} onChange={e => setAddForm({ ...addForm, role: e.target.value })} className="h-9 px-2 border border-[#E6EAF0] rounded-md text-[13px]" data-testid="org-users-add-role">
                  {ROLE_OPTIONS.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
                </select>
                <Input placeholder="Phone (optional)" value={addForm.phone} onChange={e => setAddForm({ ...addForm, phone: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] text-[#71777E]">Permission template (optional — overrides role defaults)</Label>
                <select value={addForm.template_id} onChange={e => setAddForm({ ...addForm, template_id: e.target.value })} className="w-full h-9 px-2 border border-[#E6EAF0] rounded-md text-[13px]" data-testid="org-users-add-template">
                  <option value="">— Use role defaults —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name_en} ({t.allowed_modules?.length || 0} modules)</option>)}
                </select>
              </div>

              {/* AUTH METHOD SELECTOR */}
              <div className="pt-2 border-t border-[#E6EAF0]">
                <Label className="text-[11px] text-[#0E1320] font-semibold uppercase reva-mono tracking-[0.12em]">How will this user receive access?</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer text-[12px] ${addForm.auth_method === 'invite_link' ? 'border-[#00734A] bg-[#00734A]/[0.04]' : 'border-[#E6EAF0]'}`}>
                    <input type="radio" name="auth_method" checked={addForm.auth_method === 'invite_link'} onChange={() => setAddForm({ ...addForm, auth_method: 'invite_link' })} data-testid="org-users-auth-invite" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold text-[#0E1320] inline-flex items-center gap-1.5"><Send size={11} /> Invite link</div>
                      <div className="text-[10.5px] text-[#71777E] mt-0.5">User sets their own password via a 7-day link.</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer text-[12px] ${addForm.auth_method === 'password' ? 'border-[#00734A] bg-[#00734A]/[0.04]' : 'border-[#E6EAF0]'}`}>
                    <input type="radio" name="auth_method" checked={addForm.auth_method === 'password'} onChange={() => setAddForm({ ...addForm, auth_method: 'password' })} data-testid="org-users-auth-password" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold text-[#0E1320] inline-flex items-center gap-1.5"><KeyRound size={11} /> Default password</div>
                      <div className="text-[10.5px] text-[#71777E] mt-0.5">You set / generate a password and share it manually.</div>
                    </div>
                  </label>
                </div>
                {addForm.auth_method === 'password' && (
                  <div className="mt-2">
                    <Label className="text-[11px] text-[#71777E]">Password (leave blank to auto-generate a friendly one)</Label>
                    <Input
                      type="text"
                      placeholder="Auto-generate if blank"
                      value={addForm.password}
                      onChange={e => setAddForm({ ...addForm, password: e.target.value })}
                      data-testid="org-users-add-password"
                      minLength={8}
                    />
                    <div className="mt-1 text-[10.5px] text-[#71777E]">User can log in immediately and change it from Settings.</div>
                  </div>
                )}
              </div>

              <Button type="submit" disabled={adding} className="w-full" data-testid="org-users-add-submit">
                {adding ? <Loader2 className="animate-spin h-4 w-4 me-2" /> : <UserPlus className="h-4 w-4 me-2" />}
                {addForm.auth_method === 'password' ? 'Create user + set password' : 'Create user + generate invite'}
              </Button>
            </>
          )}
        </form>
      )}

      {/* Existing users */}
      {users.length === 0 ? (
        <div className="text-center py-8 text-[#71777E] text-[13px]">No users in this organization yet.</div>
      ) : users.map(u => (
        <div key={u.id} data-testid={`org-user-${u.id}`} className="rounded-xl border border-[#E6EAF0] p-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[#0E1320]">{u.full_name}</div>
              <div className="text-[11.5px] text-[#71777E] mt-0.5 inline-flex items-center gap-2">
                <span className="inline-flex items-center gap-1"><Mail size={9} /> {u.email}</span>
                {u.phone && <span className="inline-flex items-center gap-1"><Phone size={9} /> {u.phone}</span>}
              </div>
              <div className="mt-1.5 inline-flex items-center gap-1.5">
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10.5px] font-medium reva-mono uppercase tracking-[0.1em]">{u.custom_role || u.role}</span>
                {u.permission_grant?.template_id && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10.5px] font-medium reva-mono">
                    template · {u.permission_grant.template_id.replace('tpl_', '')}
                  </span>
                )}
                {u.permission_grant && !u.permission_grant.template_id && (
                  <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10.5px] font-medium reva-mono">
                    custom grant · {u.permission_grant.allowed_modules?.length || 0} modules
                  </span>
                )}
              </div>
            </div>
            <div className="inline-flex items-center gap-1">
              <button onClick={() => onResendInvite(u)} disabled={resendingId === u.id} title="Generate fresh login link"
                className="p-1.5 text-[#0E1320] hover:bg-[#F4F6F9] rounded inline-flex items-center gap-1 text-[11px]" data-testid={`org-user-resend-${u.id}`}>
                {resendingId === u.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                <span className="hidden sm:inline">Send link</span>
              </button>
              <button onClick={() => onResetPassword(u)} disabled={resetPwId === u.id} title="Generate fresh password"
                className="p-1.5 text-[#0E1320] hover:bg-[#F4F6F9] rounded inline-flex items-center gap-1 text-[11px]" data-testid={`org-user-resetpw-${u.id}`}>
                {resetPwId === u.id ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />}
                <span className="hidden sm:inline">Set password</span>
              </button>
              <button onClick={() => removeUser(u)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" data-testid={`org-user-remove-${u.id}`}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>
          {resentLink?.user_id === u.id && resentLink.url && (
            <div className="mt-2 p-2.5 rounded-lg border border-emerald-200 bg-emerald-50" data-testid={`org-user-resent-link-${u.id}`}>
              <div className="text-[11px] text-emerald-900 font-semibold inline-flex items-center gap-1.5"><Send size={11} /> Fresh login link · expires in 7 days</div>
              <div className="mt-1 flex items-center gap-1.5">
                <code className="flex-1 text-[10.5px] bg-white border border-emerald-200 px-2 py-1 rounded reva-mono break-all">{window.location.origin}{resentLink.url}</code>
                <button onClick={() => { navigator.clipboard.writeText(window.location.origin + resentLink.url); toast.success('Copied'); }} className="p-1.5 hover:bg-emerald-100 rounded">
                  <Copy size={12} />
                </button>
              </div>
              <div className="mt-1.5 text-[10.5px] text-emerald-800">Share this link with {u.email} — they'll set their own password and log in instantly.</div>
            </div>
          )}
          {resetPwResult?.user_id === u.id && resetPwResult.password && (
            <div className="mt-2 p-2.5 rounded-lg border border-amber-200 bg-amber-50" data-testid={`org-user-reset-password-${u.id}`}>
              <div className="text-[11px] text-amber-900 font-semibold inline-flex items-center gap-1.5"><KeyRound size={11} /> New default password — share securely</div>
              <div className="mt-1 flex items-center gap-1.5">
                <code className="flex-1 text-[12px] bg-white border border-amber-200 px-2 py-1.5 rounded reva-mono font-semibold tracking-wide select-all">{resetPwResult.password}</code>
                <button onClick={() => { navigator.clipboard.writeText(resetPwResult.password); toast.success('Copied'); }} className="p-1.5 hover:bg-amber-100 rounded">
                  <Copy size={12} />
                </button>
              </div>
              <div className="mt-1.5 text-[10.5px] text-amber-800">
                Share with {u.email}. They can log in immediately and change it later from Settings. <strong>This is the ONLY time this password is shown.</strong>
              </div>
            </div>
          )}
          <div className="mt-2.5 pt-2.5 border-t border-[#F1F4F7] flex items-center gap-2">
            <Label className="text-[10.5px] reva-mono uppercase tracking-[0.14em] text-[#71777E]"><ShieldCheck size={10} className="inline me-1" /> Apply template:</Label>
            <select onChange={e => applyTemplate(u.id, e.target.value)} value="" className="flex-1 h-7 px-1.5 text-[11.5px] border border-[#E6EAF0] rounded" data-testid={`org-user-apply-tpl-${u.id}`}>
              <option value="">— Choose template —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name_en}</option>)}
            </select>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────── Helpers ───────────
function Section({ title, subtitle, children }) {
  return (
    <div>
      <h3 className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">{title}</h3>
      {subtitle && <p className="mt-1 text-[11.5px] text-[#4A5263]">{subtitle}</p>}
      <div className="mt-2 space-y-1">{children}</div>
    </div>
  );
}
function KV({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-2 py-1 border-b border-[#F4F6F9] last:border-0">
      <dt className="text-[12px] text-[#71777E]">{label}</dt>
      <dd className="text-[12.5px] text-[#0E1320] font-medium reva-mono text-end">{String(value ?? '—')}</dd>
    </div>
  );
}
